import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { normalizeInitSessionConfig } from './extractSessionObject';
import type { SessionAnswer } from './sessionAnswers';
import {
  getQuestionStepEntries,
  mapPrimitiveValuesToSessionAnswers,
} from './mapPrimitiveSessionAnswersWithConfig';

export type SessionAnswersContext = Pick<
  DeviceConfigurationApi,
  'started_at' | 'created_at' | 'ended_at' | 'updated_at' | 'config'
>;

export const getSessionAnswersBaseMs = (
  session: Pick<
    DeviceConfigurationApi,
    'started_at' | 'created_at' | 'ended_at' | 'updated_at'
  >
): number => {
  const candidates: Array<string | null | undefined> = [
    session.started_at,
    session.created_at,
    session.ended_at,
    session.updated_at,
  ];
  for (const c of candidates) {
    if (c) {
      const t = new Date(c).getTime();
      if (!Number.isNaN(t)) {
        return t;
      }
    }
  }
  return Date.now();
};

export const normalizeParsedSessionAnswers = (
  parsed: unknown,
  session: SessionAnswersContext
): SessionAnswer[] => {
  const baseMs = getSessionAnswersBaseMs(session);
  const isoAt = (offsetIndex: number) =>
    new Date(baseMs + offsetIndex * 1000).toISOString();

  if (parsed === null || parsed === undefined) {
    return [];
  }

  if (typeof parsed === 'string') {
    return [
      {
        question: 'Risposta',
        answer: parsed,
        stepId: 1,
        timestamp: isoAt(0),
      },
    ];
  }

  if (Array.isArray(parsed)) {
    const allPrimitive =
      parsed.length > 0 &&
      parsed.every(
        (item): item is string | number | boolean =>
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
      );
    if (allPrimitive && session.config != null) {
      const configSteps = normalizeInitSessionConfig(session.config);
      if (getQuestionStepEntries(configSteps).length > 0) {
        return mapPrimitiveValuesToSessionAnswers(parsed, configSteps, isoAt);
      }
    }

    return parsed.flatMap((item, index): SessionAnswer[] => {
      if (
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean'
      ) {
        return [
          {
            question: `Risposta ${index + 1}`,
            answer: String(item),
            stepId: index + 1,
            timestamp: isoAt(index),
          },
        ];
      }

      if (typeof item === 'object' && item !== null) {
        const o = item as Record<string, unknown>;
        const question =
          typeof o.question === 'string'
            ? o.question
            : typeof o.label === 'string'
              ? o.label
              : '';
        let answer = '';
        if (typeof o.answer === 'string' || typeof o.answer === 'number') {
          answer = String(o.answer);
        } else if (typeof o.value === 'string' || typeof o.value === 'number') {
          answer = String(o.value);
        }
        const stepIdRaw = o.stepId ?? o.step;
        const stepId =
          typeof stepIdRaw === 'number'
            ? stepIdRaw
            : typeof stepIdRaw === 'string' && stepIdRaw !== ''
              ? Number.parseInt(stepIdRaw, 10) || index + 1
              : index + 1;

        let timestampStr: string;
        if (typeof o.timestamp === 'string' && o.timestamp.length > 0) {
          timestampStr = o.timestamp;
        } else if (typeof o.timestamp === 'number') {
          timestampStr = new Date(o.timestamp).toISOString();
        } else {
          timestampStr = isoAt(index);
        }

        const hasStructured =
          question !== '' ||
          answer !== '' ||
          'question' in o ||
          'answer' in o ||
          'stepId' in o ||
          'timestamp' in o;

        if (hasStructured) {
          return [
            {
              question: question !== '' ? question : `Voce ${index + 1}`,
              answer: answer !== '' ? answer : '--',
              stepId,
              timestamp: timestampStr,
            },
          ];
        }

        return [
          {
            question: `Voce ${index + 1}`,
            answer: JSON.stringify(o),
            stepId: index + 1,
            timestamp: isoAt(index),
          },
        ];
      }

      return [];
    });
  }

  if (typeof parsed === 'object' && parsed !== null) {
    return normalizeParsedSessionAnswers([parsed], session);
  }

  return [
    {
      question: 'Risposta',
      answer: String(parsed),
      stepId: 1,
      timestamp: isoAt(0),
    },
  ];
};

export const parseSessionAnswersJsonToRows = (
  raw: string,
  session: SessionAnswersContext
): SessionAnswer[] => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return [];
  }
  try {
    return normalizeParsedSessionAnswers(JSON.parse(trimmed), session);
  } catch {
    return [
      {
        question: 'Risposta',
        answer: raw,
        stepId: 1,
        timestamp: new Date(getSessionAnswersBaseMs(session)).toISOString(),
      },
    ];
  }
};
